/**
 * Utilities for automating toggle settings on Instagram.
 * Includes generating stage definitions.
 *
 * We can mostly reuse our existing checkbox-related utilities.
 */
import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import { InstagramRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { getCheckbox, getCurrentValueForCheckbox } from '../checkboxDetection'
import { getAttrPromise, getDomPromise, wait } from '../interruptiblePromises'

async function toggleCheckboxAndWait(label: string): Promise<void> {
  const checkbox = await getCheckbox(label, '*')

  const updatePromise = Promise.any([
    getAttrPromise(checkbox, 'aria-checked'),
    getDomPromise(document, () => true),
  ])

  if (!checkbox) {
    throw new Error(`Could not find checkbox to toggle for label ${label}`)
  }
  checkbox.click()
  await updatePromise
}

export function generateToggleStageDefs(
  recommendationKey: InstagramRecommendationKey,
  orderedSectionLabels: string[],
  sectionLabelToSaveKeyFn: (label: string) => string,
  sectionLabelToTargetValueFn: (label: string) => boolean,
  gatherStageKey: string,
  updateStageKey: string,
  undoStageKey: string,
  url: string,
) {
  const gatherStageDefs: StageDef[] = [
    {
      type: StageType.ACTION,
      key: gatherStageKey,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
      url,
      action: async (machineContext, userContext) => {
        const { updateStoredInitialValues } = userContext

        const currentValues = await Promise.all(
          orderedSectionLabels.map((x) => getCurrentValueForCheckbox(x)),
        )

        const keyValuePairs = currentValues.map((value, i) => [
          sectionLabelToSaveKeyFn(orderedSectionLabels[i]),
          value,
        ])

        await updateStoredInitialValues(
          Object.fromEntries(keyValuePairs),
          recommendationKey,
        )
      },
    },
  ]

  const updateStageDefs: StageDef[] = [
    {
      type: StageType.ACTION,
      key: updateStageKey,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
      url,
      action: async (machineContext, userContext) => {
        const { isDry, settingsSelected } = userContext

        // Introduce a wait; some pages seem to require additional loading
        // before you can interact with the toggles
        await getCurrentValueForCheckbox(orderedSectionLabels[0])
        await wait(1000)

        for (let ix = 0; ix < orderedSectionLabels.length; ix += 1) {
          const label = orderedSectionLabels[ix]
          const saveKey = sectionLabelToSaveKeyFn(label)

          const isSelected = settingsSelected[saveKey]

          const currentValue = await getCurrentValueForCheckbox(label)
          const targetValue = sectionLabelToTargetValueFn(label)

          if (!isDry && isSelected && currentValue !== targetValue) {
            await toggleCheckboxAndWait(label)
          }
        }
      },
    },
  ]

  const undoStageDefs: StageDef[] = [
    {
      type: StageType.ACTION,
      key: undoStageKey,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
      url,
      action: async (machineContext, userContext) => {
        const { isDry, initialValues, settingsSelected } = userContext

        // Introduce a wait; some pages seem to require additional loading
        // before you can interact with the toggles
        await getCurrentValueForCheckbox(orderedSectionLabels[0])
        await wait(1000)

        for (let ix = 0; ix < orderedSectionLabels.length; ix += 1) {
          const label = orderedSectionLabels[ix]
          const saveKey = sectionLabelToSaveKeyFn(label)

          const isSelected = settingsSelected[saveKey]

          const currentValue = await getCurrentValueForCheckbox(label)
          const targetValue = initialValues[saveKey]

          if (!isDry && isSelected && currentValue !== targetValue) {
            await toggleCheckboxAndWait(label)
          }
        }
      },
    },
  ]

  return {
    gatherStageDefs,
    updateStageDefs,
    undoStageDefs,
  }
}
